import React, { useState, useEffect } from 'react';
import { appConfig } from '../appConfig';
import { authHeaders } from '../services/oidc';
import Spinner from '../components/Spinner';

const VoicemailPlayer = ({ match }) => {
  const id = match.params.id;
  const [error, setError] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);

  const audioUrl = `${appConfig.api.v2.baseUrl}${appConfig.api.v2.endpoints.voicemails}/${id}`;

  const handleError = () => {
    setError('Sorry, there was an error playing the voicemail audio. Please reach out to Tech Support for help.');
  };

  // Fetched into a blob rather than handed straight to <audio src>, because a
  // browser will not attach an Authorization header to a media element. The
  // gatekeeper injected it upstream; without it this request arrives
  // unauthenticated and the endpoint — which streams caller audio — rejects it.
  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    const loadVoicemail = async () => {
      try {
        const response = await fetch(audioUrl, { headers: await authHeaders() });
        if (!response.ok) {
          throw new Error(`Voicemail request failed with ${response.status}`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setAudioSrc(objectUrl);
      } catch (requestError) {
        if (!cancelled) handleError();
      }
    };

    loadVoicemail();

    return () => {
      cancelled = true;
      // Otherwise the blob is retained for the lifetime of the page.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [audioUrl]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          {error}
        </div>
      </div>
    );
  }

  if (!audioSrc) {
    return (
      <div style={styles.container}>
        <Spinner />
        <div style={styles.instructionMessage}>Loading your voicemail…</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.instructionMessage}>
        Press the ▶ play button (triangle) below to hear your voicemail
      </div>

      <audio
        src={audioSrc}
        controls
        onError={handleError}
        style={styles.audio}
      />
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#ffffff',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  audio: {
    width: '100%',
    maxWidth: '500px'
  },
  errorBox: {
    color: '#C62828',
    backgroundColor: '#FFEBEE',
    padding: '20px',
    borderRadius: '16px',
    fontSize: '18px',
    maxWidth: '500px',
  },
  instructionMessage: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '20px',
  }
};

export default VoicemailPlayer;
